import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TeamMember } from '@nemo/api';
import {
  MEMBER_ROLE_CLASSES,
  MEMBER_ROLE_CLASS_LABELS,
  type MemberRoleClass,
} from '@nemo/core/model';
import { useMemo, useState } from 'react';
import { UserMinus, UserPlus, UserRound, UsersRound } from 'lucide-react';
import { actionsCell, singlePicklistCell, type GridAction } from '../grid/cells/index.js';
import { orderedColumns, sortRows, type GridColumnDef, type Sort } from '../grid/columns.js';
import { DataGrid } from '../grid/DataGrid.js';
import { DensityToggle } from '../grid/DensityToggle.js';
import { readSelection, readText, text } from '../grid/fundColumns.js';
import type { PicklistOption } from '../grid/cells/PicklistEditor.js';
import { useGridPreferences } from '../grid/useGridPreferences.js';
import { gridTheme } from '../palette.js';
import { affinityPersonUrl } from '../portfolio/format.js';
import { useTRPC } from '../trpc.js';
import { formatDay } from '../ui/format.js';
import { SegmentedControl, type Segment } from '../ui/SegmentedControl.js';
import { useToast } from '../ui/Toast.js';
import { AddMemberModal, MarkLeftModal } from './MemberModals.js';

const PREFERENCES_KEY = 'nemo.grid.team.v1';

/** Who is shown: the people here now, the ones who have left, or both. */
type Shelf = 'current' | 'former' | 'all';

/** Which role class is shown. */
type RoleShelf = 'any' | MemberRoleClass;

const ROLE_OPTIONS: readonly PicklistOption[] = [
  { id: '', label: 'Not set', hint: 'counts as investment' },
  ...MEMBER_ROLE_CLASSES.map((roleClass) => ({
    id: roleClass,
    label: MEMBER_ROLE_CLASS_LABELS[roleClass],
    hint: roleClass === 'support' ? 'relationships do not count as a way in' : undefined,
  })),
];

function roleLabel(member: TeamMember): string {
  return member.roleClass === null ? '' : MEMBER_ROLE_CLASS_LABELS[member.roleClass];
}

const CURRENT_ACTIONS: readonly GridAction[] = [{ id: 'left', label: 'Mark as left' }];
const FORMER_ACTIONS: readonly GridAction[] = [{ id: 'reactivate', label: 'Reactivate' }];

interface TeamContext {
  affinityWebBase: string | null;
}

/** A former member's row reads faint, the way their relationships do elsewhere. */
function faint(member: TeamMember) {
  return member.status === 'former'
    ? { themeOverride: { textDark: gridTheme().textLight ?? '' } }
    : {};
}

function day(value: string | null): string {
  return value === null ? '' : (formatDay(value) ?? value);
}

const COLUMNS: readonly GridColumnDef<TeamMember, TeamContext>[] = [
  {
    id: 'name',
    title: 'Name',
    width: 220,
    cell: (member) => ({ ...text(member.name, false), ...faint(member) }),
    text: (member) => member.name,
    sortKey: (member) => member.name.toLowerCase(),
  },
  {
    id: 'status',
    title: 'Status',
    width: 100,
    cell: (member) => ({
      ...text(member.status === 'current' ? 'Current' : 'Former', false),
      ...faint(member),
    }),
    text: (member) => (member.status === 'current' ? 'Current' : 'Former'),
    sortKey: (member) => member.status,
  },
  {
    id: 'title',
    title: 'Title',
    width: 200,
    note: 'Their job title. Typing one chooses the role from it (an assistant is support); change the role beside it to override.',
    cell: (member) => ({ ...text(member.title ?? '', true), ...faint(member) }),
    text: (member) => member.title ?? '',
    sortKey: (member) => member.title,
    editsOnEnter: true,
  },
  {
    id: 'role',
    title: 'Role',
    width: 170,
    note: 'Investment and Platform / Operations relationships count as a way into a fund. Support (executive and team assistants, office management) stay on file, labelled, and never count. Not set counts as investment.',
    cell: (member) => singlePicklistCell(member.roleClass ?? '', ROLE_OPTIONS),
    text: roleLabel,
    sortKey: roleLabel,
    editsOnEnter: true,
  },
  {
    id: 'joined',
    title: 'Joined',
    width: 110,
    cell: (member) => ({ ...text(day(member.joinedOn), false), ...faint(member) }),
    text: (member) => member.joinedOn ?? '',
    sortKey: (member) => member.joinedOn,
  },
  {
    id: 'left',
    title: 'Left',
    width: 110,
    cell: (member) => ({ ...text(day(member.leftOn), false), ...faint(member) }),
    text: (member) => member.leftOn ?? '',
    sortKey: (member) => member.leftOn,
  },
  {
    id: 'email',
    title: 'Email',
    width: 260,
    cell: (member) => ({ ...text(member.email ?? '', false), ...faint(member) }),
    text: (member) => member.email ?? '',
    sortKey: (member) => member.email,
  },
  {
    id: 'affinity',
    title: 'Affinity',
    width: 120,
    note: 'The Affinity person they are linked to, matched by address. One not yet linked is looked up by the next Affinity sync.',
    cell: (member, context) => {
      const url = affinityPersonUrl(context.affinityWebBase, member.affinityPersonId);
      return {
        ...text(member.affinityPersonId === null ? 'Not linked yet' : 'Open', false),
        ...(url === null ? {} : { cursor: 'pointer' }),
        ...faint(member),
      };
    },
    text: (member) => member.affinityPersonId ?? '',
    sortKey: (member) => (member.affinityPersonId === null ? 1 : 0),
    links: (member, context) => {
      const url = affinityPersonUrl(context.affinityWebBase, member.affinityPersonId);
      return url === null ? [] : [{ label: 'Affinity', href: url }];
    },
  },
  {
    id: 'relationships',
    title: 'Relationships',
    width: 120,
    note: 'Every relationship on file for them; hover a cell for how many are high quality: strong or warm, or in touch in the last 2 years and not cold, with somebody now at a tracked fund.',
    cell: (member) => ({
      ...text(`${member.relationshipCount}`, false),
      contentAlign: 'right',
      ...faint(member),
    }),
    text: (member) => String(member.relationshipCount),
    sortKey: (member) => member.relationshipCount,
  },
  {
    id: 'soleFunds',
    title: 'Only way in',
    width: 320,
    note: 'Tracked funds where they are the only member who counts with a decent relationship. For somebody who left, or support staff: the funds only they and others like them know.',
    cell: (member) => ({
      ...text(soleFundsText(member), false),
      ...(member.soleFunds.length > 0 ? { cursor: 'pointer' } : {}),
      ...faint(member),
    }),
    text: soleFundsText,
    sortKey: (member) => member.soleFunds.length,
    links: (member) =>
      member.soleFunds.map((fund) => ({ label: fund.name, href: `/?fund=${fund.id}` })),
  },
  {
    id: 'actions',
    title: '',
    width: 150,
    cell: (member) => actionsCell(member.status === 'current' ? CURRENT_ACTIONS : FORMER_ACTIONS),
    text: () => '',
  },
];

function soleFundsText(member: TeamMember): string {
  const count = member.soleFunds.length;
  if (count === 0) return '';
  const names = member.soleFunds.map((fund) => fund.name);
  return `${count} · ${names.slice(0, 3).join(', ')}${count > 3 ? ', …' : ''}`;
}

/**
 * Who is on the Project A team. Their relationships are what "Who knows whom"
 * counts; when somebody leaves, everything they knew stays on file, labelled
 * as theirs, and stops counting as a way in, as does an assistant's. Anybody
 * signed in can add a member, mark one as left, bring one back or set a
 * role; each change is logged with who made it.
 */
export function TeamPage(): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const team = useQuery(trpc.members.team.queryOptions());
  const links = useQuery(trpc.portfolio.links.queryOptions());
  const [shelf, setShelf] = useState<Shelf>('current');
  const [roleShelf, setRoleShelf] = useState<RoleShelf>('any');
  const [sort, setSort] = useState<Sort | null>(null);
  const [adding, setAdding] = useState(false);
  const [leaving, setLeaving] = useState<TeamMember | null>(null);
  const preferences = useGridPreferences(PREFERENCES_KEY);

  // Who is here decides every fund's "Who knows" and its flag, so those reload too.
  const refresh = () => queryClient.invalidateQueries();
  const add = useMutation(
    trpc.members.add.mutationOptions({
      onSuccess: (added) => {
        setAdding(false);
        toast(
          `${added.name} is on the team${added.affinityLinked ? ', linked to Affinity' : '; the next Affinity sync links them'}.`,
          { tone: 'info' },
        );
        void refresh();
      },
      onError: (error) => toast(error.message),
    }),
  );
  const markLeft = useMutation(
    trpc.members.markLeft.mutationOptions({
      onSuccess: (left) => {
        setLeaving(null);
        toast(`${left.name} is marked as having left.`, { tone: 'info' });
        void refresh();
      },
      onError: (error) => toast(error.message),
    }),
  );
  const reactivate = useMutation(
    trpc.members.reactivate.mutationOptions({
      onSuccess: (back) => {
        toast(`${back.name} is on the team again.`, { tone: 'info' });
        void refresh();
      },
      onError: (error) => toast(error.message),
    }),
  );

  const setRole = useMutation(
    trpc.members.setRole.mutationOptions({
      onSuccess: (set) => {
        toast(
          `${set.name}: ${set.roleClass === null ? 'no role class (counts as investment)' : MEMBER_ROLE_CLASS_LABELS[set.roleClass]}${set.title === null ? '' : `, ${set.title}`}.`,
          { tone: 'info' },
        );
        void refresh();
      },
      onError: (error) => toast(error.message),
    }),
  );

  const all = team.data ?? [];
  const currentCount = all.filter((member) => member.status === 'current').length;
  const formerCount = all.length - currentCount;
  const shown = all
    .filter((member) => shelf === 'all' || member.status === shelf)
    .filter((member) => roleShelf === 'any' || member.roleClass === roleShelf);
  const columns = useMemo(() => orderedColumns(COLUMNS, preferences), [preferences]);
  const context = useMemo(
    () => ({ affinityWebBase: links.data?.affinityWebBase ?? null }),
    [links.data],
  );
  const rows = useMemo(
    () => sortRows(shown, columns, sort, context),
    [shown, columns, sort, context],
  );

  const roleShelves: readonly Segment<RoleShelf>[] = [
    { value: 'any', label: 'Any role' },
    ...MEMBER_ROLE_CLASSES.map((roleClass) => ({
      value: roleClass,
      label: MEMBER_ROLE_CLASS_LABELS[roleClass],
    })),
  ];

  const shelves: readonly Segment<Shelf>[] = [
    { value: 'current', label: `Current (${currentCount})`, icon: UserRound },
    { value: 'former', label: `Former (${formerCount})`, icon: UserMinus },
    { value: 'all', label: 'All', icon: UsersRound },
  ];

  function edited(
    member: TeamMember,
    columnId: string,
    value: Parameters<typeof readText>[0],
  ): void {
    if (columnId === 'title') {
      const title = readText(value);
      if (title !== null) setRole.mutate({ id: member.id, title: title.trim() || null });
    } else if (columnId === 'role') {
      const picked = readSelection(value, 'singlePicklist');
      if (picked === null) return;
      const roleClass = MEMBER_ROLE_CLASSES.find((candidate) => candidate === picked) ?? null;
      setRole.mutate({ id: member.id, roleClass });
    }
  }

  function act(member: TeamMember, action: string): void {
    switch (action) {
      case 'left':
        setLeaving(member);
        return;
      case 'reactivate':
        if (!reactivate.isPending) reactivate.mutate({ id: member.id });
        return;
    }
  }

  const failed = team.isError ? team.error.message : null;

  return (
    <div className="page">
      <div className="toolbar">
        <strong className="count">
          {team.data === undefined ? 'Team' : `${currentCount} on the team`}
        </strong>
        <span className="subtle">
          Their relationships are who knows whom. Somebody who left, or an assistant, keeps theirs
          on file, labelled, and it stops counting as a way in.
        </span>
        <span className="spacer" />
        <SegmentedControl
          label="Which members"
          value={shelf}
          options={shelves}
          onChange={(next) => {
            setShelf(next);
            setSort(null);
          }}
        />
        <SegmentedControl
          label="Which roles"
          value={roleShelf}
          options={roleShelves}
          onChange={(next) => {
            setRoleShelf(next);
            setSort(null);
          }}
        />
        <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
        <button className="button" type="button" onClick={() => setAdding(true)}>
          <UserPlus aria-hidden="true" />
          Add member
        </button>
      </div>
      <DataGrid
        rows={rows}
        columns={columns}
        context={context}
        rowId={(member) => member.id}
        preferences={preferences}
        sort={{ value: sort, onChange: setSort }}
        onAction={act}
        onCellEdited={(member, column, value) => edited(member, column.id, value)}
        rowMenu={{
          label: 'Member',
          items: (member) =>
            (member.status === 'current' ? CURRENT_ACTIONS : FORMER_ACTIONS).map((action) => ({
              label: action.label,
              onSelect: () => act(member, action.id),
            })),
        }}
        tip={(member, column) =>
          column.id === 'relationships' && member.relationshipCount > 0
            ? `${member.decentCount} high quality`
            : null
        }
        freezeColumns={1}
        empty={
          <span className="subtle">
            {failed !== null
              ? `Could not load the team: ${failed}`
              : team.isPending
                ? 'Loading the team…'
                : shelf === 'former'
                  ? 'Nobody has left.'
                  : 'Nobody here yet.'}
          </span>
        }
      />
      {adding && (
        <AddMemberModal
          pending={add.isPending}
          onAdd={(member) => add.mutate(member)}
          onCancel={() => setAdding(false)}
        />
      )}
      {leaving !== null && (
        <MarkLeftModal
          member={leaving}
          pending={markLeft.isPending}
          onConfirm={(leftOn) => markLeft.mutate({ id: leaving.id, leftOn })}
          onCancel={() => setLeaving(null)}
        />
      )}
    </div>
  );
}
